import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { ProductChangeRequestCatalogPartsApi } from "src/api/generated/erp/productChanges/api/productChangeRequestCatalogPartsApi.ts";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { ProductChangeRequestCatalogPartView } from "src/api/generated/erp/db/types/tables/productChangeRequestCatalogPartView.ts";
import { integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ConnectCatalogPartsToProductChangeRequestDialog } from "src/components/views/erp/productChanges/requests/catalogParts/ConnectCatalogPartsToProductChangeRequestDialog.tsx";
import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { FormCommonProps } from "../../../../../common/forms/types.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ProductChangeRequestCatalogPartsDataGridProps {
	productChangeRequestId: number;
}

export const ProductChangeRequestCatalogPartsDataGrid = ({
	productChangeRequestId,
}: ProductChangeRequestCatalogPartsDataGridProps) => {
	const { openDialog } = useGenericDialog();

	return (
		<ClientSideDataGridModel
			gridId={"4A7CC23F2D1A6E7B"}
			initialParams={{}}
			fetchData={() =>
				ProductChangeRequestCatalogPartsApi.getConnectedCatalogParts({
					productChangeRequestId,
				})
			}
			getRows={(data) => data}
			getRowId={(row) => row.productChangeRequestCatalogPartId}
			customFunctionUiActionView={"PRODUCT_CHANGE_REQUEST_CATALOG_PARTS"}
			render={({ dataGridProps, refreshData }) => (
				<FormCrudDataGrid<ProductChangeRequestCatalogPartView>
					columns={[
						integerColumn({
							field: "catalogPartId",
							headerName: i18n.t("id"),
							width: 90,
						}),
						textColumn({
							field: "partNo",
							headerName: i18n.t("part_no"),
							width: 160,
						}),
						textColumn({
							field: "catalogPartDescription_1",
							headerName: i18n.t("description"),
							width: 220,
						}),
						textColumn({
							field: "catalogPartCategoryName",
							headerName: i18n.t("category"),
							width: 160,
						}),
						textColumn({
							field: "impact",
							headerName: i18n.t("impact"),
							width: 300,
						}),
					]}
					form={{
						editEnabled: true,
						addRowEnabled: false,
						dialogTitle: i18n.t("impact"),
						dialogSize: "sm",
						agentContext: (row) => ({
							key: defineAgentUiContextKey(
								"erp.productChanges.productChangeRequestCatalogPart.formDialog",
							),
							title: i18n.t("impact"),
							data: {
								productChangeRequestId: productChangeRequestId,
								productChangeRequestCatalogPartId: row?.productChangeRequestCatalogPartId ?? null,
								catalogPartId: row?.catalogPartId ?? null,
							},
						}),
						component: ({ row, onCompleted }) =>
							row == null ? null : (
								<ImpactForm productChangeRequestCatalogPart={row} onCompleted={onCompleted} />
							),
					}}
					remove={{
						type: "enabled",
						action: async ({ items }) => {
							await ProductChangeRequestCatalogPartsApi.deleteConnections({
								productChangeRequestCatalogPartIds: items.map(
									(r) => r.productChangeRequestCatalogPartId,
								),
							});
						},
					}}
					actionBarComponents={
						<HorizontalBox sx={{ gap: 2 }}>
							<AsyncButton
								icon={faLink}
								label={i18n.t("connect")}
								variant={"outlined"}
								onClick={() => {
									openDialog(({ closeDialog }) => ({
										title: i18n.t("connect"),
										size: "lg",
										agentContext: {
											key: defineAgentUiContextKey(
												"erp.productChanges.connectCatalogParts.dialog",
											),
											title: i18n.t("connect"),
											data: {
												productChangeRequestId: productChangeRequestId,
											},
										},
										content: (
											<ConnectCatalogPartsToProductChangeRequestDialog
												productChangeRequestId={productChangeRequestId}
												onCompleted={async () => {
													await closeDialog();
													await refreshData();
												}}
											/>
										),
									}));
								}}
							/>
						</HorizontalBox>
					}
					agentContext={{
						key: defineAgentUiContextKey("erp.productChanges.productChangeRequestCatalogParts.grid"),
						data: {
							productChangeRequestId: productChangeRequestId,
						},
						rowFields: [
							"productChangeRequestCatalogPartId",
							"productChangeRequestId",
							"catalogPartId",
							"partNo",
						],
						selectedRowPathSegment: (row) => row.partNo,
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
};

interface ImpactFormProps extends FormCommonProps<number> {
	productChangeRequestCatalogPart: ProductChangeRequestCatalogPartView;
}

const ImpactForm = ({ productChangeRequestCatalogPart, onCompleted, onFormEdited }: ImpactFormProps) => {
	return (
		<AavoForm<{ impact: string }, number>
			defaultValues={{ impact: productChangeRequestCatalogPart.impact }}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.productChanges.productChangeRequestCatalogPartImpact.form"),
				data: {
					productChangeRequestCatalogPartId:
						productChangeRequestCatalogPart.productChangeRequestCatalogPartId,
					productChangeRequestId: productChangeRequestCatalogPart.productChangeRequestId,
					catalogPartId: productChangeRequestCatalogPart.catalogPartId,
				},
			}}
			render={({ control }) => (
				<FormTextField control={control} name={"impact"} label={i18n.t("impact")} multiline minRows={4} />
			)}
			submit={async (values) => {
				await ProductChangeRequestCatalogPartsApi.updateImpact({
					productChangeRequestCatalogPartId:
						productChangeRequestCatalogPart.productChangeRequestCatalogPartId,
					impact: values.impact,
				});
				return productChangeRequestCatalogPart.productChangeRequestCatalogPartId;
			}}
		/>
	);
};
